
import { ref, Ref } from "vue";
import { Component, Vue, Watch, Prop } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";

import { Debounce } from "@/helpers";

import CInfiniteScroll from "@/components/InfiniteScroll.vue";
import CStructure, { IStructureProp } from "@/components/Structure.vue";

import StructureFactory from "@/models/structure/factory";

export interface IProgramAssignmentAvailableWorkplace {
  factory_id: string;
  infinity_id: string | null;
  structure_name: string;
  full_name: string;
}

@Component({
  name: "CProgramAssignmentsEditWorkplaces",
  components: {
    CInfiniteScroll,
    CStructure
  }
})
export default class CProgramAssignmentsEditWorkplaces extends Vue {
  @Prop({ required: true }) public workplace_ids!: string[];
  @Prop({ required: true }) public factories!: StructureFactory[];

  public selected_factory: StructureFactory | null = null;
  public structure: IStructureProp = {};

  private workplaces_is_loading: boolean = false;
  private workplaces_current_page: number = 0;
  private workplaces_next_page: boolean = true;
  private workplaces_per_page: number = 100;
  public workplaces_search: string = "";
  public available_workplaces: IProgramAssignmentAvailableWorkplace[] = [];
  public selected_workplaces: Ref<Set<string>> = ref(new Set<string>());

  public show_filter: boolean = false;
  public filters_count: number = 0;
  public filters: Map<string, boolean> = new Map();

  protected async created() {
    this.selected_workplaces.value = new Set(this.workplace_ids);

    await this.clearWorkplaces();
  }

  private async clearWorkplaces() {
    this.available_workplaces = [];

    await this.loadWorkplaces();
  }

  public async clearFilters() {
    this.filters.clear();

    this.selected_factory = null;
    this.structure = {};

    this.filters_count = this.filters.size;

    await this.clearWorkplaces();
  }

  public async changeFactoryFilter() {
    if (this.selected_factory) {
      this.filters.set("factory", true);
    } else {
      this.filters.delete("factory");
    }

    this.filters_count = this.filters.size;

    await this.clearWorkplaces();
  }

  public async changeStructure(structure: IStructureProp) {
    this.structure = structure;

    if (this.structure?.infinity_id) {
      this.filters.set("infinity", true);
    } else {
      this.filters.delete("infinity");
    }

    this.filters_count = this.filters.size;

    await this.clearWorkplaces();
  }

  @Watch("workplaces_search")
  @Debounce(500)
  protected async searchWorkplaces() {
    await this.clearWorkplaces();
  }

  public selectedWorkplaceId(workplace: IProgramAssignmentAvailableWorkplace) {
    return this.selected_workplaces.value.has(this.workplaceId(workplace));
  }

  public workplaceId(workplace: IProgramAssignmentAvailableWorkplace) {
    if (workplace.infinity_id?.length) {
      return `${workplace.factory_id}_${workplace.infinity_id}`;
    }

    return workplace.factory_id;
  }

  public changeSelectedWorkplaces(
    workplace: IProgramAssignmentAvailableWorkplace
  ) {
    const key = this.workplaceId(workplace);

    if (this.selected_workplaces.value.has(key)) {
      this.selected_workplaces.value.delete(key);
    } else {
      this.selected_workplaces.value.add(key);
    }

    this.$emit("change", Array.from(this.selected_workplaces.value));
  }

  public async workplacesInfiniteHandler() {
    if (this.workplaces_next_page && !this.workplaces_is_loading) {
      await this.loadWorkplaces(this.workplaces_current_page + 1);
    }
  }

  private async loadWorkplaces(page: number = 0) {
    this.workplaces_is_loading = true;

    return this.$api
      .get(program_routes.assignment_available_workplaces, {
        params: {
          page,
          per_page: this.workplaces_per_page,
          search: this.workplaces_search,
          factory_id: this.selected_factory?.id,
          infinity_id: this.structure?.infinity_id,
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(
        ({
          data: res
        }: {
          data: {
            structures: IProgramAssignmentAvailableWorkplace[];
            next_page: boolean;
          };
        }) => {
          this.available_workplaces.push(...res.structures);

          this.workplaces_current_page = page;
          this.workplaces_next_page = res.next_page;
        }
      )
      .finally(() => {
        this.workplaces_is_loading = false;
      });
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
